import { isEmpty } from "lodash";

import { DashboardComponent } from "@/api";

export function getSelectedWorkspaces({
  selectedWorkspaceIds,
  currentWorkspaceId,
}: {
  selectedWorkspaceIds?: string[] | Record<string, string> | string;
  currentWorkspaceId: number;
}) {
  if (isEmpty(selectedWorkspaceIds)) {
    return [currentWorkspaceId];
  }
  if (Array.isArray(selectedWorkspaceIds)) {
    return selectedWorkspaceIds.map(Number);
  }
  if (typeof selectedWorkspaceIds === "object") {
    return Object.values(selectedWorkspaceIds).map(Number);
  }
  if (typeof selectedWorkspaceIds === "string") {
    return selectedWorkspaceIds?.split(",").map(Number);
  }

  return [];
}

const WIDGET_LOCAL_STORAGE_KEY = "widget-params";
export function widgetSessionStorageKey(customizeKey?: string) {
  return WIDGET_LOCAL_STORAGE_KEY + customizeKey;
}

const CHART_COLORS = [
  "#8F92F9",
  "#95D5F0",
  "#F042F0",
  "#9C001A",
  "#17B575",
  "#6161C3",
  "#6EC8F9",
  "#6D8CF9",
  "#F95939",
  "#FF879B",
  "#FEB3FE",
  "#791779",
  "#E7F8FF",
] as const;

const LOW_MEDIUM_HIGH_COLORS = ["#66AA22", "#FF9900", "#EB2E4E"] as const;

export function getChartColor({
  index,
  hasLowMediumHigh,
}: {
  index: number;
  hasLowMediumHigh?: boolean;
}) {
  const colors = hasLowMediumHigh
    ? [...LOW_MEDIUM_HIGH_COLORS, ...CHART_COLORS]
    : CHART_COLORS;

  return colors[index % colors.length];
}

export function formatWidgetData({
  widgetData,
  essentialSeriesKeys,
  hasLowMediumHigh,
}: {
  widgetData: DashboardComponent["data"];
  essentialSeriesKeys: string[];
  hasLowMediumHigh?: boolean;
}) {
  if (!widgetData) {
    return {
      keys: [],
      data: [],
      colors: [],
    };
  }

  const ranges = Object.keys(widgetData);
  const otherSourceKeys = new Set<string>();

  const mutatedData = ranges.map((range) => {
    const sources = Object.keys(widgetData[range]);

    const total = sources.reduce((acc, source) => {
      if (!essentialSeriesKeys.includes(source)) {
        otherSourceKeys.add(source);
      }

      return acc + widgetData[range][source];
    }, 0);

    return {
      name: range,
      total,
      ...widgetData[range],
    };
  });

  const keys = [...essentialSeriesKeys, ...Array.from(otherSourceKeys)];

  const colors = keys.map((key, index) => ({
    key,
    color: getChartColor({ index, hasLowMediumHigh }),
  }));

  return {
    keys,
    data: mutatedData,
    colors,
  };
}
